import useModal from './useModal'
import { notification } from 'antd'
import { ConfirmModal, ModalTitle, ReasonModal } from 'components/atoms'
import { IConfigModalConfirm } from 'interfaces'
import { IResponse } from 'interfaces/services'
import { useTranslation } from 'react-i18next'
import { capitalizeFirstLetter } from 'utils'

const useModalConfirm = (title?: string, ignoreToast?: boolean) => {
  const modal = useModal()
  const { t } = useTranslation()
  const openConfirmDelete = (
    message: string,
    actionApi: () => Promise<any>
  ) => {
    modal.open(
      t(title || 'do-you-want', { record: message }),
      {
        title: (
          <ModalTitle preIcon="/images/ic-delete.svg" title="confirm-delete" />
        )
      },
      (isAccept) => {
        if (isAccept)
          actionApi().then((res) => {
            if (message && res?._successfully)
              openToast(res, capitalizeFirstLetter(message))
          })
      }
    )
  }

  const openConfirmDeleteWithReason = (
    message: string,
    actionApi: (reason: string) => Promise<IResponse<any>>
  ) => {
    modal.open(
      <ReasonModal />,
      {
        title: (
          <ModalTitle
            preIcon="/images/ic-delete.svg"
            title={title || 'confirm-delete'}
          />
        ),
        footer: null
      },
      (reason) => {
        if (reason)
          actionApi(reason).then((res) => {
            openToast(res, message)
          })
      }
    )
  }

  const onConfirmDelete = (
    config: IConfigModalConfirm,
    actionApi: (reason: string) => Promise<IResponse<any>>
  ) => {
    modal.open(
      <ConfirmModal
        icon={config.icon || '/images/ic-cancel.svg'}
        description={config.description || 'cannot-undo'}
        title={t(config.title || '')}
        okLabel={config.okLabel || 'confirm'}
        allowClickOutsideBtn
        showReason
        colorBtnOk="red"
      />,
      {
        title: '',
        footer: null,
        closeIcon: <></>
      },
      (reason) => {
        if (reason)
          actionApi(reason).then((res) => {
            openToast(res, config.message || '', !config?.messageSuccess)
          })
      }
    )
  }

  const onConfirmAccept = async (
    config: IConfigModalConfirm,
    actionApi: (data?: any) => Promise<IResponse<any>>,
    onCancel?: () => void
  ) => {
    modal.open(
      <ConfirmModal
        icon={config?.icon || '/images/ic-accept.svg'}
        description={config.description}
        allowClickOutsideBtn
        title={t(config.title || '')}
        titleCustom={t(config.titleCustom || '')}
        descriptionCustom={t(config.descriptionCustom || '')}>
        {config?.children}
      </ConfirmModal>,
      {
        title: '',
        footer: null,
        closeIcon: <></>
      },
      (isAccept) => {
        if (isAccept) {
          actionApi().then((res) => {
            if (res?._successfully) {
              openToast(
                res,
                config.message || config.messageSuccess || '',
                !config?.messageSuccess
              )
            }
            return res
          })
        } else {
          if (onCancel) onCancel()
        }
      }
    )
  }

  const openToast = (
    res: IResponse<any>,
    message: string,
    isGenerateMessage = true
  ) => {
    if (res._successfully) {
      if (!ignoreToast) {
        notification.success({
          message: isGenerateMessage
            ? `${t(message)} ${t('success').toLowerCase()}`
            : t(message)
        })
      }
    }
  }

  return {
    openConfirmDelete,
    openConfirmDeleteWithReason,
    onConfirmDelete,
    onConfirmAccept
  }
}

export default useModalConfirm
